import { useState } from "react";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitTrade } from "@/lib/api";
import FlyerCard from "@/components/FlyerCard";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-ink/20 bg-cream px-4 py-3 text-sm outline-none placeholder:text-sand/70 focus:border-kwila";

const BUSINESS_TYPES = ["Builder", "Joiner / Cabinetmaker", "Door manufacturer", "Timber merchant", "Reseller", "Specifier / Designer", "Other"];
const INTERESTS = ["Timber supply", "Solid timber doors", "Outdoor furniture", "Decking & posts", "Bulk / project supply"];

const BENEFITS = [
  "Priority trade pricing on timber packs and door components",
  "Quote turnaround from our Archerfield warehouse team",
  "Kiln-dried stock direct from PNG group manufacturing",
  "Project and cutting-list supply for builders and specifiers",
];

export default function Trade() {
  const [form, setForm] = useState({
    business_name: "", abn: "", contact_person: "", email: "", phone: "",
    business_type: BUSINESS_TYPES[0], volume: "", interests: [], message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (i) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitTrade(form);
      setDone(true);
      toast.success("Trade application received", { description: "Our team will review and respond shortly." });
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="trade-page">
      <section className="bg-ink px-5 pb-20 pt-32 text-cream md:px-10 md:pt-44">
        <div className="mx-auto grid max-w-[1500px] items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Kicker dark>Trade & Reseller</Kicker>
            <h1 className="mt-4 max-w-4xl font-display text-5xl font-light leading-[0.95] tracking-tight md:text-7xl">
              <MaskedLine delay={0.1}>Trade supply,</MaskedLine>
              <MaskedLine delay={0.22} className="italic">without the run-around.</MaskedLine>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/70">
              For builders, joiners, door manufacturers, timber merchants, resellers and specifiers. Apply for a
              trade account or send a project quote request with your cutting list.
            </p>
            <Reveal delay={0.3} className="mt-8 max-w-md">
              <FlyerCard dark />
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="overflow-hidden rounded-t-[160px]">
                <img
                  src="/trade-hero.png"
                  alt="130mm Rosewood hardwood flooring, freshly finished"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-5 py-24 md:px-10">
        <div className="mx-auto grid max-w-[1500px] gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>Why Trade With Us</Kicker>
              <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">
                Direct from the mill group
              </h2>
              <ul className="mt-8 space-y-5">
                {BENEFITS.map((b, i) => (
                  <li key={b} className="flex items-start gap-4">
                    <span className="font-display text-2xl text-kwila">0{i + 1}</span>
                    <span className="pt-1 text-sm leading-relaxed text-ink/75 md:text-base">{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <Reveal className="flex h-full flex-col items-center justify-center rounded-sm border border-forest/30 bg-forest/5 p-12 text-center" data-testid="trade-success">
                <CheckCircle2 size={40} className="text-forest" />
                <p className="mt-4 font-display text-4xl">Application received.</p>
                <p className="mt-3 max-w-md text-sm text-ink/65">
                  Thank you — our trade team will review your application and be in touch to set up your account.
                </p>
              </Reveal>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={submit} className="rounded-sm border border-ink/15 bg-stone/40 p-6 md:p-10" data-testid="trade-application-form">
                  <h2 className="font-display text-3xl tracking-tight">Trade account application</h2>
                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    <input data-testid="trade-business-name-input" required placeholder="Business name *" value={form.business_name} onChange={set("business_name")} className={inputCls} />
                    <input data-testid="trade-abn-input" placeholder="ABN" value={form.abn} onChange={set("abn")} className={inputCls} />
                    <input data-testid="trade-contact-person-input" required placeholder="Contact person *" value={form.contact_person} onChange={set("contact_person")} className={inputCls} />
                    <input data-testid="trade-email-input" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                    <input data-testid="trade-phone-input" required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} />
                    <select data-testid="trade-business-type-select" value={form.business_type} onChange={set("business_type")} className={inputCls}>
                      {BUSINESS_TYPES.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    <input data-testid="trade-volume-input" placeholder="Expected monthly / annual volume" value={form.volume} onChange={set("volume")} className={`${inputCls} sm:col-span-2`} />
                  </div>
                  <p className="mb-3 mt-6 text-[11px] font-bold uppercase tracking-[0.2em] text-sand">Products of interest</p>
                  <div className="flex flex-wrap gap-2" data-testid="trade-interests">
                    {INTERESTS.map((i) => (
                      <button
                        type="button"
                        key={i}
                        data-testid={`trade-interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={() => toggleInterest(i)}
                        className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.1em] transition-colors ${
                          form.interests.includes(i) ? "bg-kwila text-cream" : "border border-ink/20 hover:bg-stone"
                        }`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                  <textarea data-testid="trade-message-input" rows={3} placeholder="Tell us about your business or upcoming projects…" value={form.message} onChange={set("message")} className={`${inputCls} mt-6`} />
                  <p className="mt-4 text-xs text-ink/50">
                    Trade references and supporting documents can be emailed after submitting. [Document upload
                    to be enabled by client.]
                  </p>
                  <button
                    data-testid="trade-submit-button"
                    disabled={busy}
                    className="mt-6 flex items-center gap-2 rounded-full bg-kwila px-8 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-ink disabled:opacity-50"
                  >
                    {busy ? "Submitting…" : "Submit Application"} <ArrowUpRight size={14} />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
